import React, { useState, useMemo } from "react";
import { CATEGORY_FORMS } from "@/lib/categoryForms";
import { MAIN_INTERESTS, CATEGORIES } from "@/lib/constants";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <label className="field-label block">
        {label} {required && <span className="text-[#E96C0F]">*</span>}
      </label>
      {children}
    </div>
  );
}

function RadioGroup({ name, options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          type="button"
          key={opt}
          data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
          data-on={value === opt}
          className="chip"
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function MultiGroup({ options, values, onChange, testIdPrefix }) {
  const toggle = (opt) => {
    const set = new Set(values || []);
    if (set.has(opt)) set.delete(opt); else set.add(opt);
    onChange(Array.from(set));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          type="button"
          key={opt}
          data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
          data-on={(values || []).includes(opt)}
          className="chip"
          onClick={() => toggle(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export default function WaitlistForm({ category, onSuccess }) {
  const categoryMeta = CATEGORIES.find((c) => c.key === category);
  const schema = CATEGORY_FORMS[category] || [];

  const [general, setGeneral] = useState({
    full_name: "", organisation: "", position: "", email: "",
    phone: "", country: "", state_city: "", website: "",
    main_interest: "",
  });
  const [cdata, setCdata] = useState({});
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setG = (k, v) => setGeneral((s) => ({ ...s, [k]: v }));
  const setC = (k, v) => setCdata((s) => ({ ...s, [k]: v }));

  const sourceParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("src") || params.get("source") || "direct";
  }, []);

  const validate = () => {
    const required = ["full_name", "organisation", "position", "email", "phone", "country", "state_city", "main_interest"];
    for (const k of required) {
      if (!general[k] || String(general[k]).trim() === "") {
        return "Please fill all required general information fields.";
      }
    }
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(general.email)) return "Please enter a valid email address.";
    if (general.phone.replace(/\D/g, "").length < 7) return "Please enter a valid phone / WhatsApp number.";
    if (!consent) return "Please tick the consent checkbox to continue.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...general,
        category,
        category_data: cdata,
        consent: true,
        source: sourceParam,
      };
      const { data } = await api.post("/submissions", payload);
      onSuccess?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="waitlist-form">
      <div className="form-section p-5 sm:p-7">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-bold tracking-widest text-[#E96C0F] uppercase">Step 2</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
        <h2 className="display text-2xl sm:text-3xl font-bold text-[#0C1F4A]">
          Your information
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Category: <strong className="text-[#0C1F4A]">{categoryMeta?.label}</strong>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <Field label="Full Name" required>
            <input data-testid="input-full-name" className="input-base" value={general.full_name}
              onChange={(e) => setG("full_name", e.target.value)} placeholder="Jane Doe" />
          </Field>
          <Field label="Organisation / Business Name" required>
            <input data-testid="input-organisation" className="input-base" value={general.organisation}
              onChange={(e) => setG("organisation", e.target.value)} placeholder="Your company" />
          </Field>
          <Field label="Position / Title" required>
            <input data-testid="input-position" className="input-base" value={general.position}
              onChange={(e) => setG("position", e.target.value)} placeholder="Founder, CEO, Director..." />
          </Field>
          <Field label="Email Address" required>
            <input data-testid="input-email" className="input-base" type="email" value={general.email}
              onChange={(e) => setG("email", e.target.value)} placeholder="you@example.com" />
          </Field>
          <Field label="Phone / WhatsApp Number" required>
            <input data-testid="input-phone" className="input-base" value={general.phone}
              onChange={(e) => setG("phone", e.target.value)} placeholder="+234 ..." />
          </Field>
          <Field label="Country" required>
            <input data-testid="input-country" className="input-base" value={general.country}
              onChange={(e) => setG("country", e.target.value)} placeholder="Nigeria" />
          </Field>
          <Field label="State / City" required>
            <input data-testid="input-state-city" className="input-base" value={general.state_city}
              onChange={(e) => setG("state_city", e.target.value)} placeholder="Lagos" />
          </Field>
          <Field label="Website / LinkedIn / Social">
            <input data-testid="input-website" className="input-base" value={general.website}
              onChange={(e) => setG("website", e.target.value)} placeholder="https://..." />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Main Interest in ATFF" required>
            <RadioGroup
              testIdPrefix="interest"
              options={MAIN_INTERESTS}
              value={general.main_interest}
              onChange={(v) => setG("main_interest", v)}
            />
          </Field>
        </div>
      </div>

      {schema.length > 0 && (
        <div className="form-section p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-bold tracking-widest text-[#E96C0F] uppercase">Step 3</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          <h2 className="display text-2xl sm:text-3xl font-bold text-[#0C1F4A]">
            Tell us more
          </h2>
          <p className="text-sm text-slate-600 mt-1">Specific to {categoryMeta?.label}.</p>
          <div className="space-y-5 mt-6">
            {schema.map((f) => (
              <Field key={f.key} label={f.label}>
                {f.type === "text" && (
                  <input data-testid={`cat-${f.key}`} className="input-base" value={cdata[f.key] || ""}
                    onChange={(e) => setC(f.key, e.target.value)} placeholder={f.placeholder || ""} />
                )}
                {f.type === "textarea" && (
                  <textarea data-testid={`cat-${f.key}`} className="input-base" value={cdata[f.key] || ""}
                    onChange={(e) => setC(f.key, e.target.value)} placeholder={f.placeholder || ""} />
                )}
                {f.type === "radio" && (
                  <RadioGroup testIdPrefix={`cat-${f.key}`} options={f.options}
                    value={cdata[f.key] || ""} onChange={(v) => setC(f.key, v)} />
                )}
                {f.type === "multi" && (
                  <MultiGroup testIdPrefix={`cat-${f.key}`} options={f.options}
                    values={cdata[f.key] || []} onChange={(v) => setC(f.key, v)} />
                )}
              </Field>
            ))}
          </div>
        </div>
      )}

      <div className="form-section p-5 sm:p-7">
        <label className="flex items-start gap-3 cursor-pointer">
          <input data-testid="consent-checkbox" type="checkbox" checked={consent}
            onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-[#F58220]" />
          <span className="text-sm text-slate-700 leading-relaxed">
            <strong className="text-[#0C1F4A]">I consent</strong> to ATFF/SMCN contacting me regarding onboarding, partnership opportunities,
            trade support, events, platform updates and related digital trade services.
          </span>
        </label>
        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
          ATFF will use the information provided to classify your interest, support onboarding, facilitate relevant follow-up
          and improve digital trade engagement. Your information will be handled in line with ATFF&apos;s Privacy Policy.
        </p>

        {error && (
          <div data-testid="form-error" className="mt-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          data-testid="submit-waitlist-btn"
          disabled={submitting}
          className="btn-orange mt-5 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-bold disabled:opacity-70"
        >
          {submitting ? (<><Loader2 size={16} className="animate-spin" /> Submitting...</>) :
                       (<><CheckCircle2 size={16} /> Submit Waitlist Registration</>)}
        </button>
      </div>
    </form>
  );
}
